import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { UserCheck, Building2, GraduationCap, ArrowRight, Check } from 'lucide-react';
import { Section } from './ui/Section';
import { Button } from './ui/Button';
const audiences = [{
  id: 'parents',
  label: 'For Parents',
  icon: UserCheck,
  title: 'The Watchtower',
  description: "Monitor payment history and property verification reports. No more 'hidden' agent fees or uncertainty about where your child is living.",
  features: ['Transparent Pricing', 'Verification Reports', 'Payment Tracking'],
  cta: 'Learn about Safety',
  color: 'bg-indigo-600'
}, {
  id: 'landlords',
  label: 'For Landlords',
  icon: Building2,
  title: 'Verified Tenants',
  description: 'Access a pool of KYC-verified students. No more chasing rent; payments are processed and tracked automatically through our platform.',
  features: ['KYC Verified Students', 'Automated Collection', 'Property Management'],
  cta: 'List Your Property',
  color: 'bg-amber-600'
}, {
  id: 'university',
  label: 'University Admin',
  icon: GraduationCap,
  title: 'Registry Access',
  description: 'A real-time dashboard of student off-campus locations for safety and emergency response. Keep your students safe, even off-campus.',
  features: ['Real-time Dashboard', 'Emergency Response', 'Safety Analytics'],
  cta: 'Partner with Us',
  color: 'bg-emerald-600'
}];
export function PartnerDashboards() {
  const [activeTab, setActiveTab] = useState(0);
  return <Section className="bg-slate-50" data-id="element-255">
      <div className="text-center mb-12" data-id="element-256">
        <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-4" data-id="element-257">
          Built for the{' '}
          <span className="text-indigo-600" data-id="element-258">Entire Ecosystem</span>
        </h2>
        <p className="text-lg text-slate-600 max-w-2xl mx-auto" data-id="element-259">
          CampusAparte isn't just for students. We provide dedicated tools for
          everyone involved in the student housing journey.
        </p>
      </div>

      <div className="flex flex-col lg:flex-row gap-8 lg:gap-12 items-start" data-id="element-260">
        {/* Navigation Tabs */}
        <div className="w-full lg:w-1/3 flex flex-col gap-2" data-id="element-261">
          {audiences.map((audience, index) => <button key={audience.id} onClick={() => setActiveTab(index)} className={`flex items-center p-4 rounded-xl text-left transition-all duration-300 ${activeTab === index ? 'bg-white shadow-md ring-1 ring-slate-200 scale-[1.02]' : 'hover:bg-white/50 text-slate-600 hover:text-slate-900'}`} data-id="element-262">
              <div className={`w-10 h-10 rounded-full flex items-center justify-center mr-4 transition-colors ${activeTab === index ? 'bg-slate-900 text-white' : 'bg-slate-200 text-slate-500'}`} data-id="element-263">
                <audience.icon className="w-5 h-5" data-id="element-264" />
              </div>
              <span className={`text-lg font-semibold ${activeTab === index ? 'text-slate-900' : 'text-slate-600'}`} data-id="element-265">
                {audience.label}
              </span>
              {activeTab === index && <motion.div layoutId="active-indicator" className="ml-auto text-amber-500" data-id="element-266">
                  <ArrowRight className="w-5 h-5" data-id="element-267" />
                </motion.div>}
            </button>)}
        </div>

        {/* Content Area */}
        <div className="w-full lg:w-2/3 min-h-[400px]" data-id="element-268">
          <AnimatePresence mode="wait" data-id="element-269">
            <motion.div key={activeTab} initial={{
            opacity: 0,
            x: 20
          }} animate={{
            opacity: 1,
            x: 0
          }} exit={{
            opacity: 0,
            x: -20
          }} transition={{
            duration: 0.3
          }} className="bg-white rounded-2xl p-8 md:p-12 shadow-lg border border-slate-100 h-full flex flex-col justify-center" data-id="element-270">
              <div className="flex items-center gap-3 mb-6" data-id="element-271">
                <span className={`px-3 py-1 rounded-full text-xs font-bold text-white uppercase tracking-wider ${audiences[activeTab].color}`} data-id="element-272">
                  {audiences[activeTab].label}
                </span>
              </div>

              <h3 className="text-3xl font-bold text-slate-900 mb-4" data-id="element-273">
                {audiences[activeTab].title}
              </h3>

              <p className="text-xl text-slate-600 mb-8 leading-relaxed" data-id="element-274">
                {audiences[activeTab].description}
              </p>

              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-10" data-id="element-275">
                {audiences[activeTab].features.map((feature, idx) => <div key={idx} className="flex items-center gap-2 text-slate-700 font-medium" data-id="element-276">
                    <div className="w-6 h-6 rounded-full bg-green-100 text-green-600 flex items-center justify-center flex-shrink-0" data-id="element-277">
                      <Check className="w-3.5 h-3.5" data-id="element-278" />
                    </div>
                    {feature}
                  </div>)}
              </div>

              <div data-id="element-279">
                <Button variant="outline" className="group" data-id="element-280">
                  {audiences[activeTab].cta}
                  <ArrowRight className="ml-2 w-4 h-4 transition-transform group-hover:translate-x-1" data-id="element-281" />
                </Button>
              </div>
            </motion.div>
          </AnimatePresence>
        </div>
      </div>
    </Section>;
}